import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import {
	DateString,
	ListItem,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	PipeSeparator
} from "client/Components";
import { SelectableItemOptions, useUserTimezone } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	forwardRef,
	Fragment,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { DocumentListItem } from "../DocumentListItem";
import { WebDavDocumentAction, WebDavDocumentActionContext } from "./actions";
import { GridItem } from "./WebDavOverview";
import { TimeZone } from "client/DateAndTime/timezones";
import { DocumentNoResult } from "../DocumentNoResult";

export type WebDavListViewHandle = {
	resetScrollPosition: () => void;
};

export type WebDavListViewProps = {
	isBusy: boolean;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	items: readonly GridItem[];
	isInSelectMode: boolean;
	onEnableSelectMode(): void;
	onOpenItem: (item: FileSystemItemModel) => void;
	selectionActions: readonly WebDavDocumentAction[];
	actionContext: WebDavDocumentActionContext;
	hasSearch: boolean;
};

export const WebDavListView = forwardRef(
	(props: WebDavListViewProps, ref: Ref<WebDavListViewHandle>): ReactElement => {
		const {
			isBusy,
			selectionFor,
			items,
			isInSelectMode,
			onEnableSelectMode,
			onOpenItem,
			selectionActions,
			actionContext,
			hasSearch
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const listHandle: RefObject<ScrollableVirtualListHandle<GridItem> | null> = useRef<ScrollableVirtualListHandle<GridItem>>(null);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					listHandle.current
						?.scrollToTop();
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<ScrollableVirtualList
				items={items}
				estimatedHeightOfAnItem={64}
				itemKey={getUniqueKey}
				ref={listHandle}
				renderAfterItems={
					<>
						<ScrollableVirtualList.LoadMoreIndicator
							isLoadingData={isBusy}
							hasMoreItems={false}
							onRequestMoreItems={() => { /* NOOP */}}
						/>
						<ScrollableVirtualList.MobilePlusMenuSpacing />
					</>
				}
				trackingId="webdav-list-view"
			>
				{(item: GridItem): ReactElement => (
					<DocumentListItem
						name={item.name}
						additionalInformation={<AdditionalInformation item={item} timezone={userTimezone} />}
						documentKind={item.model.isFolder ? DocumentItemType.Folder : DocumentItemType.File}
						extension={item.extension}
						isBookmarked={Boolean(item.model.bookmarkId)}
						isInSelectMode={isInSelectMode}
						selectable={selectionFor(item)}
						onEnableSelectMode={onEnableSelectMode}
						onOpenItem={() => onOpenItem(item.model)}
						actions={selectionActions}
						actionContext={{ ...actionContext, selectedDocuments: [item.model] }}
					/>
				)}
			</ScrollableVirtualList>
		);
	}
);
WebDavListView.displayName = "WebDavListView";

function getUniqueKey(item: GridItem): string {
	return item.id;
}

type AdditionalInformationProps = {
	item: GridItem;
	timezone: TimeZone;
};

export const AdditionalInformation = (props: AdditionalInformationProps): ReactElement | null => {
	const {
		item,
		timezone
	} = props;

	const result: ReactElement[] = [];

	if (item.modifiedDate) {
		result.push((
			<>
				<ListItem.SubTitleIcon kind="pen" />
				<DateString
					value={item.modifiedDate}
					format="RelativeTimestamp"
					timezone={timezone}
				/>
			</>
		));
	}

	if (isNotNil(item.size)) {
		result.push((
			<>
				{getDisplayFriendlySize(item.size)}
			</>
		));
	}

	if (result.length === 0) {
		return null;
	}

	return (
		<ListItem.Row>
			{result.map((x, i) => (
				<Fragment key={i}>
					{i > 0
						? (
							<PipeSeparator />
						): undefined
					}
					{ x }
				</Fragment>
			))}
		</ListItem.Row>
	);
};
AdditionalInformation.displayName = "AdditionalInformation";
